import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { resolveImageUrl } from "@/utils/resolveImage";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock, MapPin, UserPlus, CheckCircle, Loader2, UtensilsCrossed, BookOpen, DollarSign, ShoppingCart, Minus, Plus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

import API from "@/apiConfig";

// Helper function to convert YouTube/Vimeo URLs to embed URLs
const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

// Helper function to create Google Maps link from location
const getMapLink = (location) => {
  if (!location) return null;
  // For Word Alive default address
  const wordAliveAddress = "1120 W Panola Street, Carthage, TX 75633";
  
  // If it's just "Word Alive Global Center" or similar, use the full address
  if (location.includes("Word Alive") && !location.includes(",")) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wordAliveAddress)}`;
  }
  
  // Otherwise use the location as-is (it likely already has an address)
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

// Helper function to format location display
const formatLocation = (location) => {
  if (!location) return "Word Alive Global Center";
  
  // If it's Word Alive without full address, just show the name
  if (location.includes("Word Alive") && !location.includes(",")) {
    return location;
  }
  
  // If location has a comma, it likely has name + address
  // Split and format nicely
  const parts = location.split(",");
  if (parts.length >= 2) {
    return location; // Show full formatted location
  }
  
  return location;
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Selections for event options
  const [selectedMenu, setSelectedMenu] = useState([]);
  const [selectedFees, setSelectedFees] = useState([]);
  const [feeGuestNames, setFeeGuestNames] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  // Deep-link: auto-open event from ?event=<id> query param
  useEffect(() => {
    if (events.length === 0) return;
    const eventId = searchParams.get("event");
    if (eventId && !selectedEvent) {
      const match = events.find((e) => e.id === eventId);
      if (match) {
        setSelectedEvent(match);
        resetSelections();
        // Auto-scroll to registration form if &register=true
        if (searchParams.get("register") === "true" && match.registration_enabled) {
          setTimeout(() => {
            const form = document.querySelector('[data-testid="event-registration-form"]');
            if (form) {
              form.scrollIntoView({ behavior: "smooth", block: "start" });
              const nameInput = form.querySelector('[data-testid="reg-name"]');
              if (nameInput) nameInput.focus();
            }
          }, 400);
        }
      } else {
        toast.error("Event not found");
        setSearchParams({}, { replace: true });
      }
    }
  }, [events]); // eslint-disable-line

  // Handle return from PayPal event payment
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (!paymentStatus) return;
    // Defer toast so <Toaster> has time to mount and subscribe to sonner's store
    setTimeout(() => {
      if (paymentStatus === "success") {
        toast.success("Payment successful! You're all set. Check your email for confirmation.");
      } else if (paymentStatus === "error") {
        toast.error("Payment could not be processed. Please try registering again or contact us.");
      }
      setSearchParams({}, { replace: true });
    }, 100);
  }, []); // eslint-disable-line

  const resetSelections = () => {
    setRegForm({ name: "", email: "", phone: "", notes: "" });
    setRegDone(false);
    setSelectedMenu([]);
    setSelectedFees([]);
    setFeeGuestNames({});
    setSelectedProducts([]);
  };

  // Calculate running total
  const eventTotal = useMemo(() => {
    if (!selectedEvent) return 0;
    let total = selectedEvent.event_fee || 0;
    const menu = selectedEvent.menu_items || [];
    selectedMenu.forEach(s => {
      if (s.index >= 0 && s.index < menu.length) total += menu[s.index].price * s.qty;
    });
    const fees = selectedEvent.fees || [];
    selectedFees.forEach(idx => {
      if (idx >= 0 && idx < fees.length) total += fees[idx].price;
    });
    const prods = selectedEvent.linked_product_details || [];
    selectedProducts.forEach(s => {
      const p = prods.find(x => x.id === s.product_id);
      if (p) total += p.price * s.qty;
    });
    return total;
  }, [selectedEvent, selectedMenu, selectedFees, selectedProducts]);

  const hasOptions = selectedEvent && ((selectedEvent.event_fee || 0) > 0 || (selectedEvent.menu_items || []).length > 0 || (selectedEvent.fees || []).length > 0 || (selectedEvent.linked_product_details || []).length > 0);

  // Menu item toggle
  const toggleMenu = (index) => {
    setSelectedMenu(prev => {
      const existing = prev.find(s => s.index === index);
      if (existing) return prev.filter(s => s.index !== index);
      return [...prev, { index, qty: 1 }];
    });
  };
  const updateMenuQty = (index, qty) => {
    if (qty <= 0) { setSelectedMenu(prev => prev.filter(s => s.index !== index)); return; }
    setSelectedMenu(prev => prev.map(s => s.index === index ? { ...s, qty } : s));
  };

  // Fee toggle
  const toggleFee = (index) => {
    setSelectedFees(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };

  // Product toggle
  const toggleProduct = (pid) => {
    setSelectedProducts(prev => {
      const existing = prev.find(s => s.product_id === pid);
      if (existing) return prev.filter(s => s.product_id !== pid);
      return [...prev, { product_id: pid, qty: 1 }];
    });
  };
  const updateProductQty = (pid, qty) => {
    if (qty <= 0) { setSelectedProducts(prev => prev.filter(s => s.product_id !== pid)); return; }
    setSelectedProducts(prev => prev.map(s => s.product_id === pid ? { ...s, qty } : s));
  };

  const handleRegister = async () => {
    if (!regForm.name || !regForm.email) {
      toast.error("Please enter your name and email");
      return;
    }
    const fees = selectedEvent?.fees || [];
    for (const idx of selectedFees) {
      if (fees[idx]?.collect_guest_name && !(feeGuestNames[idx] || "").trim()) {
        toast.error(`Please enter the ${fees[idx].guest_name_label || "guest name"} for "${fees[idx].name}"`);
        return;
      }
    }
    setRegLoading(true);
    try {
      if (hasOptions) {
        const feesPayload = selectedFees.map((idx) => fees[idx]?.collect_guest_name ? { index: idx, guest_name: (feeGuestNames[idx] || "").trim() } : idx);
        const body = {
          ...regForm,
          selected_menu: selectedMenu,
          selected_fees: feesPayload,
          selected_products: selectedProducts,
        };
        const res = await axios.post(`${API}/events/${selectedEvent.id}/pay`, body);
        const data = res.data;

        if (data.approval_url) {
          // Paid registration → redirect to PayPal (Giving account)
          toast.success("Redirecting to PayPal to complete payment...");
          window.location.href = data.approval_url;
          return;
        }
        // Free registration (total == 0)
        setRegDone(true);
        toast.success("Successfully registered!");
      } else {
        // Simple registration (no options)
        await axios.post(`${API}/events/${selectedEvent.id}/register`, regForm);
        setRegDone(true);
        toast.success("Successfully registered!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setRegLoading(false);
    }
  };

  const eventDates = events.map((e) => {
    const [y, m, d] = e.date.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d));
  });

  // Helper to check if event is in the past
  const isEventPast = (eventDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = eventDate.split("-");
    const evDate = new Date(Number(y), Number(m) - 1, Number(d));
    return evDate < today;
  };

  // Filter events: show ALL future events + past events within 5 days
  const fiveDaysAgo = new Date();
  fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
  fiveDaysAgo.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const recentEvents = events.filter((e) => {
    const [y, m, d] = e.date.split("-");
    const evDate = new Date(Number(y), Number(m) - 1, Number(d));
    // Show if: future event OR recent past event (within 5 days)
    return evDate >= today || evDate >= fiveDaysAgo;
  });

  const filteredEvents = selectedDate
    ? recentEvents.filter((e) => {
        const [y, m, d] = e.date.split("-");
        const evDate = new Date(Number(y), Number(m) - 1, Number(d));
        return evDate.toDateString() === selectedDate.toDateString();
      })
    : recentEvents;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-navy py-16 text-white">
        <div className="container-page text-center">
          <div className="gold-line-center mb-4" />
          <h1 className="font-heading text-4xl sm:text-5xl mb-4">Events Calendar</h1>
          <p className="text-white/60 text-lg">Stay connected with what's happening at Word Alive Global Center</p>
        </div>
      </section>

      <div className="container-page py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Calendar */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-sm shadow-sm p-6 border border-bone-dark">
              <Calendar
                data-testid="event-calendar"
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                month={month}
                onMonthChange={setMonth}
                modifiers={{ hasEvent: eventDates }}
                modifiersStyles={{
                  hasEvent: { backgroundColor: "#D4AF37", color: "#0A1A2F", fontWeight: "700", borderRadius: "2px" },
                }}
                className="w-full"
              />
              {selectedDate && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing events for {selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                  <Button data-testid="clear-date-btn" variant="ghost" size="sm" className="text-gold" onClick={() => setSelectedDate(null)}>Show All</Button>
                </div>
              )}
            </div>
          </div>

          {/* Event List */}
          <div className="lg:col-span-7" data-testid="events-list">
            <h2 className="font-heading text-2xl text-navy mb-6">
              {selectedDate ? `Events on ${selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}` : "All Upcoming Events"}
            </h2>
            {filteredEvents.length === 0 && <p className="text-gray-500">No events found for this date.</p>}
            <div className="flex flex-col gap-4">
              {filteredEvents.map((event) => {
                const isPast = isEventPast(event.date);
                return (
                  <Card 
                    key={event.id} 
                    className={`border-l-4 ${isPast ? 'border-gray-400 bg-gray-50' : 'border-gold bg-white'} card-hover cursor-pointer ${isPast ? 'opacity-75' : ''}`}
                    onClick={() => { setSelectedEvent(event); resetSelections(); setSearchParams({ event: event.id }, { replace: true }); }} 
                    data-testid={`event-card-${event.id}`}
                  >
                    <CardContent className="p-5 flex gap-4">
                      {event.video_url && getEmbedUrl(event.video_url) ? (
                        <div className="w-32 h-24 shrink-0 rounded-sm overflow-hidden bg-black" onClick={(e) => e.stopPropagation()}>
                          <iframe
                            src={getEmbedUrl(event.video_url)}
                            className="w-full h-full"
                            allowFullScreen
                            title={event.title}
                          />
                        </div>
                      ) : event.image_url ? (
                        <div className={`w-32 h-24 shrink-0 ${isPast ? 'bg-gray-200' : 'bg-gray-100'} rounded-sm overflow-hidden`}>
                          <img 
                            src={resolveImageUrl(event.image_url)} 
                            alt={event.title}
                            className={`w-full h-full object-cover ${isPast ? 'grayscale' : ''}`}
                          />
                        </div>
                      ) : (
                        <div className={`${isPast ? 'bg-gray-500' : 'bg-navy'} text-white rounded-sm px-3 py-2 text-center min-w-[60px] shrink-0`}>
                          <p className={`text-xs uppercase ${isPast ? 'text-gray-300' : 'text-gold'}`}>
                            {new Date(event.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}
                          </p>
                          <p className="text-2xl font-bold font-heading">{new Date(event.date + "T12:00:00").getDate()}</p>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-heading text-lg ${isPast ? 'text-gray-600' : 'text-navy'}`}>{event.title}</h3>
                          {isPast && (
                            <Badge variant="outline" className="border-gray-400 text-gray-600 rounded-sm text-xs">
                              Past Event
                            </Badge>
                          )}
                        </div>
                        {event.time && <p className={`${isPast ? 'text-gray-400' : 'text-gray-500'} text-sm flex items-center gap-1 mb-1`}><Clock className="h-3 w-3" /> {event.time}</p>}
                        <div className="flex items-center gap-3 flex-wrap">
                          <a 
                            href={getMapLink(event.location)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`${isPast ? 'text-gray-400 hover:text-gray-600' : 'text-gray-500 hover:text-gold'} text-sm flex items-center gap-1 transition-colors underline decoration-dotted underline-offset-2`}
                          >
                            <MapPin className="h-3 w-3" /> {formatLocation(event.location)}
                          </a>
                          {event.registration_enabled && !isPast && (
                            <Badge className="bg-gold/15 text-gold border border-gold/30 rounded-sm text-xs"><UserPlus className="h-3 w-3 mr-1" />Register</Badge>
                          )}
                          {(event.event_fee || 0) > 0 && (
                            <Badge variant="outline" className={`${isPast ? 'border-gray-300 text-gray-500' : 'border-green-300 text-green-700'} rounded-sm text-xs`}><DollarSign className="h-3 w-3 mr-0.5" />${event.event_fee.toFixed(2)}</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => { setSelectedEvent(null); resetSelections(); setSearchParams({}, { replace: true }); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">{selectedEvent?.title}</DialogTitle>
            <DialogDescription className="sr-only">Event details and registration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedEvent?.video_url && getEmbedUrl(selectedEvent.video_url) ? (
              <div className="w-full rounded-sm overflow-hidden aspect-video bg-black">
                <iframe
                  src={getEmbedUrl(selectedEvent.video_url)}
                  className="w-full h-full"
                  allowFullScreen
                  title={selectedEvent.title}
                />
              </div>
            ) : selectedEvent?.image_url && (
              <div className={`w-full ${selectedEvent && isEventPast(selectedEvent.date) ? 'bg-gray-200' : 'bg-gray-100'} rounded-sm overflow-hidden flex items-center justify-center`}>
                <img 
                  src={resolveImageUrl(selectedEvent.image_url)} 
                  alt={selectedEvent.title}
                  className={`w-full h-auto max-h-[70vh] object-contain ${selectedEvent && isEventPast(selectedEvent.date) ? 'grayscale' : ''}`}
                />
              </div>
            )}
            <div className="flex items-center gap-4 flex-wrap">
              <Badge className={selectedEvent && isEventPast(selectedEvent.date) ? "bg-gray-500 text-white rounded-sm" : "btn-navy"}>
                <CalendarDays className="h-3 w-3 mr-1" />
                {selectedEvent && new Date(selectedEvent.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </Badge>
              {selectedEvent && isEventPast(selectedEvent.date) && (
                <Badge variant="outline" className="border-gray-400 text-gray-600 rounded-sm">Past Event</Badge>
              )}
              {selectedEvent?.registration_enabled && selectedEvent && !isEventPast(selectedEvent.date) && (
                <Badge className="bg-gold text-navy rounded-sm"><UserPlus className="h-3 w-3 mr-1" /> Registration Open</Badge>
              )}
            </div>
            {selectedEvent?.registration_enabled && selectedEvent && !isEventPast(selectedEvent.date) && (
              <button
                data-testid="copy-registration-link"
                className="flex items-center gap-1.5 text-xs text-navy/60 hover:text-gold transition-colors"
                onClick={() => {
                  const url = `${window.location.origin}/events?event=${selectedEvent.id}&register=true`;
                  navigator.clipboard.writeText(url).then(() => toast.success("Registration link copied!"));
                }}>
                <Link2 className="h-3 w-3" /> Copy registration link
              </button>
            )}
            {selectedEvent?.time && <p className="text-gray-600 flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {selectedEvent.time}</p>}
            <a 
              href={getMapLink(selectedEvent?.location)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gold flex items-center gap-2 transition-colors group"
            >
              <MapPin className="h-4 w-4 text-gold" /> 
              <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                {formatLocation(selectedEvent?.location)}
              </span>
            </a>
            <p className="text-gray-700 leading-relaxed">{selectedEvent?.description}</p>

            {/* Registration Form */}
            {selectedEvent?.registration_enabled && selectedEvent && !isEventPast(selectedEvent.date) && (
              <>
                <Separator />
                {regDone ? (
                  <div className="text-center py-4" data-testid="registration-success">
                    <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-2" />
                    <p className="font-heading text-lg text-navy">You're Registered!</p>
                    <p className="text-gray-500 text-sm">We look forward to seeing you there.</p>
                  </div>
                ) : (
                  <div data-testid="event-registration-form">
                    <h4 className="font-heading text-lg text-navy mb-3 flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-gold" /> Register for this Event
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-navy text-sm">Full Name *</Label>
                        <Input data-testid="reg-name" value={regForm.name} onChange={(e) => setRegForm(f => ({ ...f, name: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Email *</Label>
                        <Input data-testid="reg-email" type="email" value={regForm.email} onChange={(e) => setRegForm(f => ({ ...f, email: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Phone</Label>
                        <Input data-testid="reg-phone" type="tel" value={regForm.phone} onChange={(e) => setRegForm(f => ({ ...f, phone: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Notes</Label>
                        <Textarea data-testid="reg-notes" value={regForm.notes} onChange={(e) => setRegForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="border-bone-dark focus:border-gold rounded-sm" placeholder="Dietary needs, accessibility, etc." />
                      </div>

                      {/* Event Options */}
                      {hasOptions && (
                        <>
                          {/* Base Fee */}
                          {(selectedEvent.event_fee || 0) > 0 && (
                            <div className="bg-gold/5 border border-gold/20 rounded-sm p-3" data-testid="event-base-fee">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-4 w-4 text-gold" />
                                  <span className="text-sm font-medium text-navy">Registration Fee</span>
                                </div>
                                <span className="font-semibold text-navy">${selectedEvent.event_fee.toFixed(2)}</span>
                              </div>
                            </div>
                          )}

                          {/* Menu Items */}
                          {(selectedEvent.menu_items || []).length > 0 && (
                            <div data-testid="event-menu-section">
                              <div className="flex items-center gap-2 mb-2">
                                <UtensilsCrossed className="h-4 w-4 text-gold" />
                                <Label className="text-navy font-semibold text-sm">Food Menu</Label>
                              </div>
                              <div className="space-y-2">
                                {selectedEvent.menu_items.map((item, idx) => {
                                  const sel = selectedMenu.find(s => s.index === idx);
                                  return (
                                    <div key={idx} className={`flex items-center justify-between p-2.5 rounded-sm border transition-colors cursor-pointer ${sel ? "bg-gold/10 border-gold/30" : "bg-white border-bone-dark hover:border-gold/20"}`} onClick={() => toggleMenu(idx)} data-testid={`menu-option-${idx}`}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${sel ? "bg-gold border-gold" : "border-gray-300"}`}>
                                          {sel && <CheckCircle className="h-3 w-3 text-white" />}
                                        </div>
                                        <span className="text-sm">{item.name}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">${item.price.toFixed(2)}</span>
                                        {sel && (
                                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                            <button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateMenuQty(idx, sel.qty - 1)} data-testid={`menu-qty-minus-${idx}`}><Minus className="h-3 w-3" /></button>
                                            <span className="text-xs w-5 text-center font-medium">{sel.qty}</span>
                                            <button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateMenuQty(idx, sel.qty + 1)} data-testid={`menu-qty-plus-${idx}`}><Plus className="h-3 w-3" /></button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Additional Fees */}
                          {(selectedEvent.fees || []).length > 0 && (
                            <div data-testid="event-fees-section">
                              <div className="flex items-center gap-2 mb-2">
                                <BookOpen className="h-4 w-4 text-gold" />
                                <Label className="text-navy font-semibold text-sm">Additional Options</Label>
                              </div>
                              <div className="space-y-2">
                                {selectedEvent.fees.map((fee, idx) => {
                                  const isSelected = selectedFees.includes(idx);
                                  return (
                                    <div key={idx}>
                                      <div className={`flex items-center justify-between p-2.5 rounded-sm border transition-colors cursor-pointer ${isSelected ? "bg-gold/10 border-gold/30" : "bg-white border-bone-dark hover:border-gold/20"}`} onClick={() => toggleFee(idx)} data-testid={`fee-option-${idx}`}>
                                        <div className="flex items-center gap-2">
                                          <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${isSelected ? "bg-gold border-gold" : "border-gray-300"}`}>
                                            {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                          </div>
                                          <div>
                                            <span className="text-sm">{fee.name}</span>
                                            {fee.description && <p className="text-xs text-gray-400">{fee.description}</p>}
                                          </div>
                                        </div>
                                        <span className="text-sm font-medium">${fee.price.toFixed(2)}</span>
                                      </div>
                                      {isSelected && fee.collect_guest_name && (
                                        <Input
                                          data-testid={`fee-guest-input-${idx}`}
                                          placeholder={fee.guest_name_label || "Guest's Name"}
                                          value={feeGuestNames[idx] || ""}
                                          onChange={(e) => setFeeGuestNames((p) => ({ ...p, [idx]: e.target.value }))}
                                          onClick={(e) => e.stopPropagation()}
                                          className="mt-1.5 text-sm"
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Linked Products */}
                          {(selectedEvent.linked_product_details || []).length > 0 && (
                            <div data-testid="event-products-section">
                              <div className="flex items-center gap-2 mb-2">
                                <ShoppingCart className="h-4 w-4 text-gold" />
                                <Label className="text-navy font-semibold text-sm">Available Products</Label>
                              </div>
                              <div className="space-y-2">
                                {selectedEvent.linked_product_details.map((p) => {
                                  const sel = selectedProducts.find(s => s.product_id === p.id);
                                  return (
                                    <div key={p.id} className={`flex items-center justify-between p-2.5 rounded-sm border transition-colors cursor-pointer ${sel ? "bg-gold/10 border-gold/30" : "bg-white border-bone-dark hover:border-gold/20"} ${!p.in_stock ? "opacity-50 pointer-events-none" : ""}`} onClick={() => p.in_stock && toggleProduct(p.id)} data-testid={`product-option-${p.id}`}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${sel ? "bg-gold border-gold" : "border-gray-300"}`}>
                                          {sel && <CheckCircle className="h-3 w-3 text-white" />}
                                        </div>
                                        {p.image_url && <img src={resolveImageUrl(p.image_url)} alt="" className="w-8 h-8 rounded-sm object-cover" />}
                                        <div>
                                          <span className="text-sm">{p.name}</span>
                                          {!p.in_stock && <p className="text-xs text-red-500">Out of stock</p>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">${p.price.toFixed(2)}</span>
                                        {sel && (
                                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                            <button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateProductQty(p.id, sel.qty - 1)}><Minus className="h-3 w-3" /></button>
                                            <span className="text-xs w-5 text-center font-medium">{sel.qty}</span>
                                            <button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateProductQty(p.id, sel.qty + 1)}><Plus className="h-3 w-3" /></button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Total */}
                          <div className="bg-navy/5 border border-navy/10 rounded-sm p-3" data-testid="event-total">
                            <div className="flex items-center justify-between">
                              <span className="font-heading text-navy">Total</span>
                              <span className="font-heading text-xl text-navy">${eventTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        data-testid="reg-submit-btn"
                        className="w-full btn-gold uppercase tracking-wider font-medium"
                        onClick={handleRegister}
                        disabled={regLoading}
                      >
                        {regLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                        {regLoading ? "Registering..." : eventTotal > 0 ? `Register & Checkout ($${eventTotal.toFixed(2)})` : "Register Now"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Past Event Message */}
            {selectedEvent && isEventPast(selectedEvent.date) && selectedEvent.registration_enabled && (
              <>
                <Separator />
                <div className="text-center py-4 bg-gray-50 rounded-sm">
                  <p className="text-gray-600 text-sm">Registration for this event has closed.</p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
